/**
 * Validation Utilities
 * 
 * Input validation functions for transcript editor
 */

/**
 * Validate speaker name
 * @param {string} speaker - Speaker name to validate
 * @returns {Object} Validation result
 */
export const validateSpeaker = (speaker) => {
  if (!speaker || typeof speaker !== 'string') {
    return {
      valid: false,
      error: 'Speaker name is required',
    };
  }

  const trimmed = speaker.trim();

  if (trimmed.length === 0) {
    return {
      valid: false,
      error: 'Speaker name cannot be empty',
    };
  }

  if (trimmed.length > 50) {
    return {
      valid: false,
      error: 'Speaker name must be less than 50 characters',
    };
  }

  return { valid: true };
};

/**
 * Validate block text
 * @param {string} text - Block text to validate
 * @returns {Object} Validation result
 */
export const validateBlockText = (text) => {
  if (typeof text !== 'string') {
    return {
      valid: false,
      error: 'Text must be a string',
    };
  }

  if (text.trim().length === 0) {
    return {
      valid: false,
      error: 'Block text cannot be empty',
    };
  }

  if (text.length > 5000) {
    return {
      valid: false,
      error: 'Block text must be less than 5000 characters',
    };
  }

  return { valid: true };
};

/**
 * Validate timestamp
 * @param {number} time - Time in seconds
 * @param {string} label - Label for error message (e.g., 'start time')
 * @returns {Object} Validation result
 */
export const validateTimestamp = (time, label = 'timestamp') => {
  if (typeof time !== 'number') {
    return {
      valid: false,
      error: `${label} must be a number`,
    };
  }

  if (isNaN(time)) {
    return {
      valid: false,
      error: `${label} is not a valid number`,
    };
  }

  if (time < 0) {
    return {
      valid: false,
      error: `${label} cannot be negative`,
    };
  }

  if (time > 86400) {
    // 24 hours
    return {
      valid: false,
      error: `${label} cannot exceed 24 hours`,
    };
  }

  return { valid: true };
};

/**
 * Validate time range
 * @param {number} start - Start time in seconds
 * @param {number} end - End time in seconds
 * @returns {Object} Validation result
 */
export const validateTimeRange = (start, end) => {
  const startValidation = validateTimestamp(start, 'Start time');
  if (!startValidation.valid) {
    return startValidation;
  }

  const endValidation = validateTimestamp(end, 'End time');
  if (!endValidation.valid) {
    return endValidation;
  }

  if (start >= end) {
    return {
      valid: false,
      error: 'End time must be greater than start time',
    };
  }

  const duration = end - start;
  if (duration < 0.1) {
    // Min 100ms
    return {
      valid: false,
      error: 'Duration must be at least 100ms',
    };
  }

  if (duration > 600) {
    // Max 10 minutes per block
    return {
      valid: false,
      error: 'Duration cannot exceed 10 minutes per block',
    };
  }

  return { valid: true };
};

/**
 * Validate search query
 * @param {string} query - Search query
 * @returns {Object} Validation result
 */
export const validateSearchQuery = (query) => {
  if (!query || typeof query !== 'string') {
    return {
      valid: false,
      error: 'Search query is required',
    };
  }

  const trimmed = query.trim();

  if (trimmed.length === 0) {
    return {
      valid: false,
      error: 'Search query cannot be empty',
    };
  }

  if (trimmed.length < 2) {
    return {
      valid: false,
      error: 'Search query must be at least 2 characters',
    };
  }

  if (trimmed.length > 200) {
    return {
      valid: false,
      error: 'Search query must be less than 200 characters',
    };
  }

  return { valid: true };
};

/**
 * Check if cursor is at start of content editable element
 * @param {HTMLElement} element - Content editable element
 * @returns {boolean} True if cursor is at start
 */
export const isCursorAtStart = (element) => {
  if (!element) return false;

  const selection = window.getSelection();
  if (!selection.rangeCount) return false;

  const range = selection.getRangeAt(0);
  return range.startOffset === 0 && range.startContainer === element.firstChild;
};

/**
 * Check if cursor is at end of content editable element
 * @param {HTMLElement} element - Content editable element
 * @returns {boolean} True if cursor is at end
 */
export const isCursorAtEnd = (element) => {
  if (!element) return false;

  const selection = window.getSelection();
  if (!selection.rangeCount) return false;

  const range = selection.getRangeAt(0);
  const lastChild = element.lastChild;

  if (!lastChild) return true;

  const textLength =
    lastChild.nodeType === Node.TEXT_NODE
      ? lastChild.textContent.length
      : lastChild.innerText?.length || 0;

  return (
    range.endOffset === textLength && range.endContainer === lastChild
  );
};

/**
 * Get cursor position in content editable element
 * @param {HTMLElement} element - Content editable element
 * @returns {number} Cursor position (character offset)
 */
export const getCursorPosition = (element) => {
  if (!element) return 0;

  const selection = window.getSelection();
  if (!selection.rangeCount) return 0;

  const range = selection.getRangeAt(0);
  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(element);
  preCaretRange.setEnd(range.endContainer, range.endOffset);

  return preCaretRange.toString().length;
};

/**
 * Set cursor position in content editable element
 * @param {HTMLElement} element - Content editable element
 * @param {number} position - Character offset
 */
export const setCursorPosition = (element, position) => {
  if (!element) return;

  const range = document.createRange();
  const selection = window.getSelection();

  try {
    let charCount = 0;
    let targetNode = null;
    let targetOffset = 0;

    const findPosition = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const nodeLength = node.textContent.length;
        if (charCount + nodeLength >= position) {
          targetNode = node;
          targetOffset = position - charCount;
          return true;
        }
        charCount += nodeLength;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          if (findPosition(node.childNodes[i])) {
            return true;
          }
        }
      }
      return false;
    };

    if (findPosition(element)) {
      range.setStart(targetNode, targetOffset);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  } catch (e) {
    console.error('Error setting cursor position:', e);
  }
};

/**
 * Sanitize HTML content (prevent XSS)
 * @param {string} html - HTML string to sanitize
 * @returns {string} Sanitized HTML
 */
export const sanitizeHTML = (html) => {
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
};


